import type { BusinessHours, DayHours, Location } from '@app/services/api.types';
import MetaConnectButton from '@app/components/meta-connect/MetaConnectButton';
import { countryOptions } from '@app/utils/constants';
import {
    Descriptions,
    Divider,
    Drawer,
    Flex,
    type DescriptionsProps,
    type DrawerProps,
} from 'antd';
import useLocation from '@app/hooks/locations/useLocation';
import { startCase } from 'lodash';
import { BUSINESS_HOURS_DAYS } from './LocationForm';

export type LocationDrawerProps = DrawerProps & {
    data: Location | null;
};

/** e.g. 1440 → "1 day", 150 → "2 hours 30 mins", 0 → "None". */
const formatBookingNotice = (totalMinutes: number) => {
    if (totalMinutes <= 0) return 'None';
    const parts = [
        { count: Math.floor(totalMinutes / (24 * 60)), unit: 'day' },
        { count: Math.floor((totalMinutes % (24 * 60)) / 60), unit: 'hour' },
        { count: totalMinutes % 60, unit: 'min' },
    ];
    return parts
        .filter(({ count }) => count > 0)
        .map(({ count, unit }) => `${count} ${unit}${count === 1 ? '' : 's'}`)
        .join(' ');
};

/** A missing or \`null\` day means closed. */
const formatDayHours = (hours?: DayHours | null) =>
    hours ? `${hours.open} – ${hours.close}` : 'Closed';

export const LocationDrawer = ({ data, ...props }: LocationDrawerProps) => {
    const { data: locationData } = useLocation({ id: data?.id?.toString() || '' });
    const minBookingNotice = locationData?.config?.find(
        (item) => item.field === 'minimum_booking_notice'
    )?.value as number | undefined;
    const businessHours = locationData?.config?.find((item) => item.field === 'business_hours')
        ?.value as Partial<BusinessHours> | undefined;

    const detailsItems: DescriptionsProps['items'] = [
        {
            key: '1',
            label: 'Name',
            children: data?.name || '-',
        },
        {
            key: '2',
            label: 'Brand',
            children: data?.brand_name || '-',
        },
        {
            key: '3',
            label: 'Channel Partner',
            children: data?.channel_name || '-',
        },
        {
            key: '4',
            label: 'Failover Phone Number',
            children: data?.failover_number || '-',
        },
        {
            key: '5',
            label: 'Admin Email',
            children: data?.contact_email || '-',
        },
        {
            key: '6',
            label: 'Admin Phone Number',
            children: data?.contact_telephone || '-',
        },
    ];

    const addressItems: DescriptionsProps['items'] = [
        {
            key: '1',
            label: 'Address Line 1',
            children: data?.address_line1 || '-',
        },
        {
            key: '2',
            label: 'Address Line 2',
            children: data?.address_line2 || '-',
        },
        {
            key: '3',
            label: 'City',
            children: data?.city || '-',
        },
        {
            key: '4',
            label: 'Country',
            children: data?.country
                ? countryOptions.find(({ value }) => value === data?.country)?.label
                : '-',
        },
        {
            key: '5',
            label: 'Post Code',
            children: data?.post_code || '-',
        },
    ];

    // Not saved / still loading → '-'
    const businessHoursItems = BUSINESS_HOURS_DAYS.map((day) => (
        <Flex key={day} gap={20}>
            <div style={{ width: 100 }}>{startCase(day)}:</div>
            <div>{businessHours ? formatDayHours(businessHours[day]) : '-'}</div>
        </Flex>
    ));

    const businessDetailsItems: DescriptionsProps['items'] = [
        {
            key: '1',
            label: 'Minimum Booking Notice',
            children:
                typeof minBookingNotice === 'number' ? formatBookingNotice(minBookingNotice) : '-',
        },
        {
            key: '2',
            label: 'Business Hours',
            children: <div>{businessHoursItems}</div>,
        },
    ];

    const renderDetailsTab = () => {
        return (
            <>
                <Descriptions
                    title="Details"
                    colon={false}
                    layout="vertical"
                    items={detailsItems}
                    column={2}
                />
                <Divider />
                <Descriptions
                    title="Address"
                    colon={false}
                    layout="vertical"
                    items={addressItems}
                    column={2}
                />
                <Divider />
                <Descriptions
                    title="Business Details"
                    colon={false}
                    layout="vertical"
                    items={businessDetailsItems}
                    column={1}
                />
                <Divider />
                <MetaConnectButton
                    key={data?.id ?? 'meta-connect'}
                    location_id={data?.id}
                    twilio_sid={data?.twilio_sid ?? null}
                />
            </>
        );
    };

    return (
        <Drawer
            title={
                <>
                    <Flex align="center" justify="space-between" gap={12}>
                        <span>Location Details</span>
                    </Flex>
                    {data?.id ? (
                        <div className="ant-drawer-header-meta">
                            <span>ID: {data?.id}</span>
                        </div>
                    ) : null}
                </>
            }
            size={700}
            closable={{ 'aria-label': 'Close Button' }}
            {...props}>
            {renderDetailsTab()}
        </Drawer>
    );
};

export default LocationDrawer;
